/**
 * Floodlit-stadium hero backdrop: a night sky lit by floodlight blooms over a striped,
 * receding pitch. Rendered as an absolute-fill behind the hero content (and the loading
 * skeleton), so it reads as a stadium without a hosted photo. The same treatment applies to
 * every match phase (pre, live, finished).
 */

import React from 'react';
import { View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Stop, Rect } from 'react-native-svg';
import {
  matchHubHeroBackgroundStyles as styles,
  STADIUM_SKY_COLORS,
  STADIUM_PITCH_COLORS,
  STADIUM_PITCH_FADE,
  STADIUM_VEIL_COLORS,
  STADIUM_VEIL_LOCATIONS,
  STADIUM_FLOOD_SIDE,
  STADIUM_FLOOD_CENTER,
  STADIUM_STRIPE_COUNT,
} from './MatchHubHeroBackground.styles';

/** Soft radial floodlight glows — two corner lights + a broad central wash. */
const Floodlights: React.FC = () => (
  <Svg style={styles.fill} width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
    <Defs>
      <RadialGradient id="matchHubFloodLeft" cx="3%" cy="-5%" r="46%">
        <Stop offset="0" stopColor={STADIUM_FLOOD_SIDE} stopOpacity="0.5" />
        <Stop offset="0.6" stopColor={STADIUM_FLOOD_SIDE} stopOpacity="0" />
      </RadialGradient>
      <RadialGradient id="matchHubFloodRight" cx="97%" cy="-5%" r="46%">
        <Stop offset="0" stopColor={STADIUM_FLOOD_SIDE} stopOpacity="0.5" />
        <Stop offset="0.6" stopColor={STADIUM_FLOOD_SIDE} stopOpacity="0" />
      </RadialGradient>
      <RadialGradient id="matchHubFloodCenter" cx="50%" cy="-20%" r="60%">
        <Stop offset="0" stopColor={STADIUM_FLOOD_CENTER} stopOpacity="0.32" />
        <Stop offset="0.62" stopColor={STADIUM_FLOOD_CENTER} stopOpacity="0" />
      </RadialGradient>
    </Defs>
    <Rect width="100" height="100" fill="url(#matchHubFloodCenter)" />
    <Rect width="100" height="100" fill="url(#matchHubFloodLeft)" />
    <Rect width="100" height="100" fill="url(#matchHubFloodRight)" />
  </Svg>
);

/** Alternating vertical mow stripes across the pitch. */
const MowStripes: React.FC = () => (
  <View style={styles.stripeRow} pointerEvents="none">
    {Array.from({ length: STADIUM_STRIPE_COUNT }, (_, i) => (
      <View
        key={i}
        style={[styles.stripe, i % 2 === 0 ? styles.stripeLight : styles.stripeDark]}
      />
    ))}
  </View>
);

export const MatchHubHeroBackground: React.FC = () => (
  <View style={styles.fill} pointerEvents="none">
    <LinearGradient colors={[...STADIUM_SKY_COLORS]} style={styles.fill} />
    <Floodlights />

    <View style={styles.pitchBand}>
      <LinearGradient colors={[...STADIUM_PITCH_COLORS]} style={styles.fill} />
      <MowStripes />
      <View style={styles.pitchLine} />
      <View style={styles.pitchCircle} />
      <LinearGradient colors={[...STADIUM_PITCH_FADE]} style={styles.pitchTopFade} />
    </View>

    <LinearGradient
      colors={[...STADIUM_VEIL_COLORS]}
      locations={[...STADIUM_VEIL_LOCATIONS]}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      style={styles.fill}
    />
  </View>
);
